// Katalog položek a varianty (F3 3.3; kontrakt tag Ceny, backend/src/moduly/katalog/routes.ts, 08 § 3.25):
//   GET    /polozky[?typ&hledat&zarizeni_id&oblibene&aktivni]  každý přihlášený; ceny jen s doklad.zobrazit_ceny;
//                                                               `muze` seznamu (zalozit, zmenit_cenu) a položek
//   GET    /polozky/{id}                                        položka s variantami, povolením na zařízeních, `muze`
//   POST   /polozky, PUT /polozky/{id}, DELETE /polozky/{id}    `katalog.upravit` (admin, jednatel, příznak účtu
//                                                               „Smí upravovat katalog položek“ — rozhodnutí 14. 9.)
//   POST   /polozky/{id}/varianty, PUT|DELETE …/{podId}         `katalog.upravit`; 409 polozka_varianta.duplicitni
//   POST   /polozky/{id}/varianty/{podId}/zverejnit[?sloucit]  `katalog.upravit`; „Přidat do katalogu“ (díl N3)
// Varianty zákazníka (díl N3, rozhodnutí 23. 10. 2026): bez `zakaznik_id` vrací server jen společné varianty — řidič,
// skladník a příjem na skladě nic nepředávají; nabídka, zakázka a ceny zákazníka předají jeho id (`zakaznikId`),
// Katalog chce všechny (`variantyZakazniku: 'vse'`).
// Výchozí cenu položky mění jen `cenik.upravit` (admin, jednatel) — jinak ji UI neposílá (server by vrátil 403).
import type { Schema } from '@ecoser/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { jeHledaniKodu, normalizovatKodOdpadu } from '../odpady';
import { api } from './klient';

export type Polozka = Schema<'PolozkaSVariantami'>;
/** Právo s důvodem (`muze` v odpovědích) — UI podle něj kreslí tlačítka a vedle neaktivních ukáže důvod. */
export type PravoKatalogu = Schema<'Pravo'>;
export type PolozkaSeznam = Schema<'PolozkaSVariantamiSeznam'>;
export type PolozkaVstup = Schema<'PolozkaVstup'>;
/** Založení položky i s variantami a výchozí cenou v jednom zápisu (okno Nová položka, 6. 10. 2026). */
export type PolozkaZalozeniVstup = Schema<'PolozkaZalozeniVstup'>;
export type Varianta = Schema<'PolozkaVarianta'>;
/** Výsledek „Přidat do katalogu“ — společná varianta, sloučení a počet přepojených řádků (díl N3). */
export type VariantaZverejneni = Schema<'PolozkaVariantaZverejneni'>;
export type VariantaVstup = Schema<'PolozkaVariantaVstup'>;
export type TypPolozky = PolozkaVstup['typ'];
export type MjPolozky = NonNullable<PolozkaVstup['mj']>;
export type PodtypPolozky = NonNullable<PolozkaVstup['podtyp']>;
/**
 * Stav skladu položky na našem zařízení (`GET /polozky?sklad=`) — jen s právem číst sklad, jinak chybí
 * (zadavatel 31. 10. 2026, našeptávač kódu se stavem skladu).
 */
export type SklademPolozky = NonNullable<Polozka['skladem']>;

/**
 * Zařízení, jehož stav skladu ukazuje našeptávač kódu u podkladu od odběratele: mostní váha CZL00673 (Kateřinská 235,
 * Stráž nad Nisou), odkud se prodává (zadavatel 31. 10. 2026).
 */
export const SKLAD_PRODEJE_ICZ = 'CZL00673';

/**
 * Kontext dotazu na stav skladu: doklad od odběratele a faktura z něj. Server s ním dá stav na CZL00673 i tomu, kdo má
 * `prodej.podklad` (účetní) a `sklad.cist` ne — jen číslo u kódu (zadavatel 1. 11. 2026, 05/4830 b).
 */
export const KONTEXT_STAVU_PODKLAD = 'podklad_prodeje';
export type KontextStavu = typeof KONTEXT_STAVU_PODKLAD;

/** Akce zápisu do katalogu (08 § 3.25, rozhodnutí 14. 9. 2026 — 05/42). */
export const AKCE_UPRAVIT_KATALOG = 'katalog.upravit';
/** Výchozí cena položky patří k ceníkům (08 § 3.25 `cenik.upravit` — admin, jednatel). */
export const AKCE_UPRAVIT_CENU = 'cenik.upravit';
/** Akce, se kterou server posílá ceny (08 § 3.3). */
export const AKCE_ZOBRAZIT_CENY = 'doklad.zobrazit_ceny';

export const KLIC_KATALOG = ['katalog'] as const;
export const VELIKOST_KATALOGU = 50;

export const TYPY_POLOZEK: readonly TypPolozky[] = [
  'odpad',
  'zbozi',
  'zpetny_odber',
  'obal',
  'sluzba',
  'material',
  'doprava',
];

/** Název typu v jednotném čísle (formulář, drobeček). */
export const NAZEV_TYPU: Readonly<Record<TypPolozky, string>> = {
  odpad: 'Odpad',
  material: 'Materiál',
  zbozi: 'Zboží',
  sluzba: 'Služba',
  doprava: 'Doprava',
  obal: 'Obal',
  zpetny_odber: 'Zpětný odběr',
};

/**
 * Vedlejší text položky ve výběru — typ, u odpadu kategorie O / N a jednotka („Odpad · N · kg“), stejně jako ostatní
 * výběry v aplikaci (nabídka 27. 10. 2026).
 */
export function popisPolozkyVeVyberu(p: {
  typ: string;
  kategorie?: string | null;
  mj: string;
}): string {
  const typ = (NAZEV_TYPU as Readonly<Record<string, string>>)[p.typ] ?? p.typ;
  return [typ, p.typ === 'odpad' ? p.kategorie : null, p.mj].filter(Boolean).join(' · ');
}

export const MJ_POLOZEK: readonly MjPolozky[] = [
  'kg',
  't',
  'ks',
  'bal',
  'hod',
  'jízda',
  'doklad',
  'přistavení',
  'měsíc',
  'náklad',
  'm³',
  'km',
];

/** Jednotky zpětného odběru (7.14): počet kusů, nebo hmotnost v kg. */
export const MJ_ZPETNEHO_ODBERU: readonly MjPolozky[] = ['ks', 'kg'];

/**
 * Měrné jednotky povolené pro typ (odpad kg/t; zpětný odběr ks nebo kg — krok 7.14, rozhodnutí zadavatele 4. a 7. 10.
 * 2026: baterie a akumulátory se vedou a oceňují v kg, elektro v kusech, ale i elektro může mít u zákazníka cenu za
 * kg); `m³` (odpadní vody, migrace 2500) mají ostatní typy.
 */
export function mjProTyp(typ: TypPolozky): readonly MjPolozky[] {
  if (typ === 'odpad') return ['kg', 't'];
  if (typ === 'zpetny_odber') return MJ_ZPETNEHO_ODBERU;
  return MJ_POLOZEK;
}

/**
 * Jednotky, ve kterých se položka smí účtovat na řádku zakázky (hlášení 13. 10. 2026, migrace 4612): hlavní MJ a další
 * povolené (`mj_alternativni`, např. odpadní voda m³ i t). Odpad a zpětný odběr další jednotky nemají; bez nich jen
 * hlavní. Zrcadlí `povoleneMjPolozky` serveru.
 */
export function povoleneMjPolozky(
  p: Pick<Polozka, 'typ' | 'mj' | 'mj_alternativni'> | null | undefined,
): string[] {
  if (!p) return [];
  const dalsi = p.typ === 'odpad' || p.typ === 'zpetny_odber' ? [] : (p.mj_alternativni ?? []);
  return [p.mj, ...dalsi.filter((m, i) => m !== p.mj && dalsi.indexOf(m) === i)];
}

/** Výchozí jednotka zpětného odběru podle podtypu (7.14): baterie a akumulátory kg, elektro (a bez podtypu) ks. */
export function vychoziMjZpetnehoOdberu(podtyp: string | null | undefined): MjPolozky {
  return podtyp === 'baterie' ? 'kg' : 'ks';
}

/**
 * Název režimu odpadních vod v UI (zadavatel 5. 10. 2026, 05/2080): zaškrtávátko i štítek „Odpadní voda v režimu
 * odpadních vod“; v úzkém seznamu katalogu zkráceně „Odpadní voda“ s plným názvem v nápovědě.
 */
export const NAZEV_ODPADNI_VODY = 'Odpadní voda v režimu odpadních vod';
export const NAZEV_ODPADNI_VODY_KRATKY = 'Odpadní voda';

/** Nejdelší poznámka (Popis) ve výběru položky — delší se zkrátí třemi tečkami. */
const DELKA_POZNAMKY_VE_VYBERU = 80;

/**
 * Doplňující řádek položky ve výběrech (zakázka, ceník, smluvní cena, nabídka): u odpadní vody štítek a Popis,
 * aby šlo víc odpadních vod od sebe rozlišit (zadavatel 5. 10. 2026: „víc takových položek s nějakou poznámkou“);
 * u zpětného odběru Popis skupiny („Do skupiny patří chladničky, mrazničky…“ — skladník podle něj pozná, kam zařízení
 * patří, 27. 10. 2026). U ostatních položek prázdné — popis služby je vnitřní poznámka.
 */
export function poznamkaVeVyberu(
  p: Pick<Polozka, 'rezim_odpadnich_vod' | 'popis'> & { typ?: string },
): string {
  if (!p.rezim_odpadnich_vod && p.typ !== 'zpetny_odber') return '';
  const popis = (p.popis ?? '').replace(/\s+/g, ' ').trim();
  // Popis skupiny zpětného odběru je důležitý (co do ní patří), proto delší než poznámka odpadní vody.
  const delka = p.rezim_odpadnich_vod ? DELKA_POZNAMKY_VE_VYBERU : 2 * DELKA_POZNAMKY_VE_VYBERU;
  const zkraceny = popis.length > delka ? `${popis.slice(0, delka - 1).trimEnd()}…` : popis;
  if (!p.rezim_odpadnich_vod) return zkraceny;
  return zkraceny ? `${NAZEV_ODPADNI_VODY_KRATKY} · ${zkraceny}` : NAZEV_ODPADNI_VODY_KRATKY;
}

export const NAZEV_PODTYPU: Readonly<Record<PodtypPolozky, string>> = {
  drť: 'drť',
  granulát: 'granulát',
  regranulát: 'regranulát',
  stavebni: 'stavební materiál',
  elektro: 'elektrozařízení',
  baterie: 'baterie a akumulátory',
};

/** Nadpis skupiny podtypu v seznamu našeptávače položky (velké písmeno na začátku). */
export const NAZEV_PODTYP_NADPIS: Readonly<Record<PodtypPolozky, string>> = {
  drť: 'Drť',
  granulát: 'Granulát',
  regranulát: 'Regranulát',
  stavebni: 'Stavební materiál',
  elektro: 'Elektrozařízení',
  baterie: 'Baterie a akumulátory',
};

/** Podtypy pro typ (materiál, zpětný odběr); ostatní typy podtyp nemají. */
export function podtypyProTyp(typ: TypPolozky): readonly PodtypPolozky[] {
  if (typ === 'material') return ['drť', 'granulát', 'regranulát', 'stavebni'];
  if (typ === 'zpetny_odber') return ['elektro', 'baterie'];
  return [];
}

/** Typy, kterým kód přidělí server, když ho uživatel nevyplní. */
export const PREFIX_KODU: Readonly<Partial<Record<TypPolozky, string>>> = {
  material: 'MAT',
  zbozi: 'ZBO',
  sluzba: 'SLU',
  doprava: 'DOP',
};

export type SadaOblibenych = 'vyroba' | 'autoservis';

export interface FiltrPolozek {
  typ: TypPolozky;
  /** Text hledání: kód (odpad i s mezerami), název nebo varianta bez diakritiky. */
  hledat?: string;
  /** Jen odpady povolené na zařízení. */
  zarizeniId?: string;
  oblibene?: SadaOblibenych;
  /** Zahrnout i neaktivní položky (výchozí jen aktivní). */
  iNeaktivni?: boolean;
  strana?: number;
  /** Velikost stránky; bez ní `VELIKOST_KATALOGU`. */
  velikost?: number;
  /** Navíc varianty tohoto zákazníka (nabídka, zakázka, ceny zákazníka — díl N3); bez něj jen společné. */
  zakaznikId?: string;
  /** Katalog: varianty všech zákazníků (`vse`), nebo jen položky, které je mají (`jen`). */
  variantyZakazniku?: 'vse' | 'jen';
  /** Řazení `pole:asc|desc` (kod, nazev, kategorie, mj); bez něj podle kódu, u zboží a zpětného odběru podle názvu. */
  razeni?: string;
  /** Jen položky, které smí dodat občan (veřejný řádek Ceníku sběrný dvůr — nabídka pro občana, 27. 10. 2026). */
  proObcana?: boolean;
  /** Jen položky platného Ceníku sběrný dvůr (hledání skladníka u příjmu na skladě — W84). */
  cenikSd?: boolean;
  /** IČZ našeho zařízení: položky ponesou `skladem` (stav skladu; server ho dá jen tomu, kdo smí číst sklad). */
  sklad?: string;
  /** Doklad od odběratele a faktura z něj: stav na CZL00673 dostane i ten, kdo má `prodej.podklad` (jen se `sklad`). */
  skladKontext?: KontextStavu;
  /** Jen položky, kterých je na zařízení `sklad` víc než 0 (oddíl „Skladem na …“); kdo stav nevidí, dostane nic. */
  jenSkladem?: boolean;
}

export function parametryPolozek(filtr: FiltrPolozek): URLSearchParams {
  const q = new URLSearchParams({
    typ: filtr.typ,
    strana: String(filtr.strana ?? 1),
    velikost: String(filtr.velikost ?? VELIKOST_KATALOGU),
    razeni:
      filtr.razeni ??
      (filtr.typ === 'zbozi' || filtr.typ === 'zpetny_odber' ? 'nazev:asc' : 'kod:asc'),
  });
  const text = filtr.hledat?.trim() ?? '';
  if (text)
    q.set(
      'hledat',
      filtr.typ === 'odpad' && jeHledaniKodu(text) ? normalizovatKodOdpadu(text) : text,
    );
  if (filtr.zarizeniId) q.set('zarizeni_id', filtr.zarizeniId);
  if (filtr.oblibene) q.set('oblibene', filtr.oblibene);
  if (!filtr.iNeaktivni) q.set('aktivni', 'true');
  if (filtr.variantyZakazniku) q.set('varianty_zakazniku', filtr.variantyZakazniku);
  else if (filtr.zakaznikId) q.set('zakaznik_id', filtr.zakaznikId);
  if (filtr.proObcana) q.set('pro_obcana', 'true');
  if (filtr.cenikSd) q.set('cenik_sd', 'true');
  if (filtr.sklad) {
    q.set('sklad', filtr.sklad);
    if (filtr.skladKontext) q.set('sklad_kontext', filtr.skladKontext);
    if (filtr.jenSkladem) q.set('jen_skladem', 'true');
  }
  return q;
}

export function nacistPolozky(filtr: FiltrPolozek, signal?: AbortSignal): Promise<PolozkaSeznam> {
  return api.get<PolozkaSeznam>(`/polozky?${parametryPolozek(filtr).toString()}`, { signal });
}

/** Kolik nejčastějších položek skladu nabídne roletka (CL.65 e — „třeba TOP 20“). */
export const POCET_NEJCASTEJSICH = 20;

/**
 * Nejčastější položky na skladě (nález CL.65 e): server je vybere ze zakázek s vykládkou u nás za posledních
 * 365 dní a seřadí od nejčastější — roletka nad hledáním, ať skladník nemusí psát kód.
 */
export function useNejcastejsiNaSkladu(typ: TypPolozky, povoleno = true) {
  return useQuery({
    queryKey: [...KLIC_KATALOG, 'nejcastejsi-sklad', typ],
    queryFn: ({ signal }) =>
      api.get<PolozkaSeznam>(
        `/polozky?${new URLSearchParams({
          typ,
          nejcastejsi: 'sklad',
          aktivni: 'true',
          velikost: String(POCET_NEJCASTEJSICH),
        }).toString()}`,
        { signal },
      ),
    enabled: povoleno,
    staleTime: 5 * 60_000,
  });
}

/** `povoleno=false` dotaz nespustí — záložka Režim DPH má vlastní přehled a seznam položek nepotřebuje. */
export function useSeznamPolozek(filtr: FiltrPolozek, povoleno = true) {
  return useQuery({
    queryKey: [...KLIC_KATALOG, 'seznam', filtr],
    queryFn: ({ signal }) => nacistPolozky(filtr, signal),
    placeholderData: keepPreviousData,
    enabled: povoleno,
  });
}

/**
 * Které varianty má detail položky nést (díl N3): bez voleb jen společné, `zakaznikId` = navíc varianty zákazníka
 * dokladu, `vsechnyVarianty` = varianty všech zákazníků se štítkem (jen Katalog položek).
 */
export interface VolbyVariant {
  zakaznikId?: string | null;
  vsechnyVarianty?: boolean;
}

function dotazVariant(volby: VolbyVariant = {}): string {
  if (volby.vsechnyVarianty) return '?varianty_zakazniku=vse';
  return volby.zakaznikId ? `?zakaznik_id=${encodeURIComponent(volby.zakaznikId)}` : '';
}

export function nacistPolozku(
  id: string,
  signal?: AbortSignal,
  volby: VolbyVariant = {},
): Promise<Polozka> {
  return api.get<Polozka>(`/polozky/${encodeURIComponent(id)}${dotazVariant(volby)}`, { signal });
}

/** Klíč detailu položky v cache — nese rozsah variant (společné / zákazníka / všechny). */
export function klicDetailuPolozky(id: string | undefined, volby: VolbyVariant = {}) {
  const rozsah = volby.vsechnyVarianty ? 'vse' : (volby.zakaznikId ?? '');
  return [...KLIC_KATALOG, 'detail', id, rozsah] as const;
}

export function usePolozka(id: string | undefined, volby: VolbyVariant = {}) {
  return useQuery({
    queryKey: klicDetailuPolozky(id, volby),
    queryFn: ({ signal }) => nacistPolozku(id ?? '', signal, volby),
    enabled: Boolean(id),
  });
}

/**
 * Normalizovaný klíč názvu varianty — stejně jako server (`nazev_klic`: mezery sloučené, ořez, malá písmena). Podle něj
 * okno pozná, že zakládaná varianta už existuje (hlídání duplicit, díl N3).
 */
export function klicVarianty(nazev: string): string {
  return nazev.replace(/\s+/g, ' ').trim().toLowerCase();
}

/**
 * Existující varianta se stejným názvem, kterou server při zakládání z řádku nabídky použije místo nové: přednost má
 * varianta zákazníka, pak společná (díl N3; server rozhoduje stejně).
 */
export function shodnaVarianta<T extends Pick<Varianta, 'nazev' | 'zakaznik_id'>>(
  varianty: readonly T[],
  nazev: string,
): T | undefined {
  const klic = klicVarianty(nazev);
  if (!klic) return undefined;
  const shodne = varianty.filter((v) => klicVarianty(v.nazev) === klic);
  return shodne.find((v) => v.zakaznik_id) ?? shodne.find((v) => !v.zakaznik_id);
}

/** Je varianta jen pro jednoho zákazníka (díl N3)? */
export const jeVariantaZakaznika = (v: Pick<Varianta, 'zakaznik_id'>): boolean =>
  Boolean(v.zakaznik_id);

/** Text varianty ve výběru: u varianty zákazníka „— varianta zákazníka“ (zákazník je ten z dokladu). */
export function textVarianty(v: Pick<Varianta, 'nazev' | 'zakaznik_id'>): string {
  return jeVariantaZakaznika(v) ? `${v.nazev} — varianta zákazníka` : v.nazev;
}

/**
 * „Přidat do katalogu“ (díl N3): varianta zákazníka se stane společnou. Shodná společná varianta = 409
 * `polozka_varianta.duplicitni` s `existujici`; `sloucit` přepojí řádky nabídek a ceny na ni a zákaznickou smaže.
 */
export function zverejnitVariantu(
  polozkaId: string,
  variantaId: string,
  sloucit = false,
): Promise<VariantaZverejneni> {
  return api.post<VariantaZverejneni>(
    `/polozky/${encodeURIComponent(polozkaId)}/varianty/${encodeURIComponent(variantaId)}/zverejnit${sloucit ? '?sloucit=true' : ''}`,
    {},
  );
}

export function zalozitPolozku(telo: PolozkaZalozeniVstup): Promise<Polozka> {
  return api.post<Polozka>('/polozky', telo);
}

export function ulozitPolozku(id: string, telo: PolozkaVstup): Promise<Polozka> {
  return api.put<Polozka>(`/polozky/${encodeURIComponent(id)}`, telo);
}

export function zalozitVariantu(polozkaId: string, telo: VariantaVstup): Promise<Varianta> {
  return api.post<Varianta>(`/polozky/${encodeURIComponent(polozkaId)}/varianty`, telo);
}

export function odebratVariantu(polozkaId: string, variantaId: string): Promise<void> {
  return api.delete<void>(
    `/polozky/${encodeURIComponent(polozkaId)}/varianty/${encodeURIComponent(variantaId)}`,
  );
}

// --- Návrhy položek od obchodníků ke schválení (rozhodnutí zadavatele 7. 10. 2026, 05/2491) ----------------------------
//   POST /polozky/navrh                       `nabidka.vytvorit` — neaktivní položka „čeká na schválení“; 409
//                                             polozka.navrh_existuje (+ `polozka`) = položka už v katalogu je
//   POST /polozky/{id}/schvalit-navrh         `katalog.upravit` — položka se stane aktivní
//   POST /polozky/{id}/zamitnout-navrh        `katalog.upravit` — s důvodem, položka zůstane neaktivní
//   GET  /polozky?navrh=ceka                  `katalog.upravit` (jinak 403) — návrhy ke schválení

export type NavrhPolozky = NonNullable<Polozka['navrh']>;

export interface NavrhPolozkyVstup {
  typ: 'odpad' | 'material' | 'sluzba' | 'zbozi';
  katalog_odpadu_id?: string;
  nazev?: string;
  mj?: string;
  popis?: string;
  citace?: string;
  poptavka_id?: string;
}

export function navrhnoutPolozku(telo: NavrhPolozkyVstup): Promise<Polozka> {
  return api.post<Polozka>('/polozky/navrh', telo);
}

export function schvalitNavrhPolozky(id: string): Promise<Polozka> {
  return api.post<Polozka>(`/polozky/${encodeURIComponent(id)}/schvalit-navrh`, {});
}

export function zamitnoutNavrhPolozky(id: string, duvod: string): Promise<Polozka> {
  return api.post<Polozka>(`/polozky/${encodeURIComponent(id)}/zamitnout-navrh`, { duvod });
}

/** Návrhy čekající na schválení — jen pro účet, který smí upravovat katalog (`povoleno`). */
export function useNavrhyPolozek(povoleno: boolean) {
  return useQuery({
    queryKey: [...KLIC_KATALOG, 'navrhy'],
    queryFn: ({ signal }) =>
      api.get<PolozkaSeznam>('/polozky?navrh=ceka&velikost=50&razeni=vytvoreno:asc', { signal }),
    enabled: povoleno,
  });
}
